/* eslint-disable camelcase */
import { prettifyMixedCase } from '@frontend/shared/Util.js';

export const buildDocumentMetadata = json => {
  const documentCount = Object.keys(json).length;
  const documentTypes = Object.values(json).flatMap(({ document_type, page_range }) => {
    const [start, end] = page_range;
    const length = end - start + 1;
    return Array(length).fill(document_type);
  });

  return {
    documentCount,
    documentTypes,
  };
};

// Convert ['person', '0', 'name'] → 'person[0].name' --> document data
export function pathPartsToFlatKey(pathParts) {
  if (!Array.isArray(pathParts)) return String(pathParts ?? '');

  const out = [];
  for (const raw of pathParts) {
    const part = String(raw);
    const isIndex = /^\d+$/.test(part);
    if (isIndex && out.length > 0) {
      out[out.length - 1] = `${out[out.length - 1]}[${part}]`;
    } else {
      out.push(part);
    }
  }
  return out.join('.');
}

// Convert 'person[0].name[1].first_name' → ['person', '0', 'name', '1', 'first_name'] --> document data
export function parseBracketPath(path) {
  if (!path) return [];
  const segments = [];
  const dotParts = String(path).split('.');

  dotParts.forEach(part => {
    const re = /([^\[]+)|\[(\d+)\]/g;
    let match;
    while ((match = re.exec(part)) !== null) {
      if (match[1]) {
        segments.push(match[1]);        // property name
      } else if (match[2]) {
        segments.push(match[2]);        // index as string ('0', '1', ...)
      }
    }
  });

  return segments;
}
export const transformDataIntoFields = json => {
  const allFields = [];

  // Keep the segment key instead of just values
  for (const [segmentKey, segmentValue] of Object.entries(json)) {
    if (!segmentValue || !segmentValue.data) continue;

    const { data: mainData, page_range } = segmentValue;
    const { data, metadata } = mainData || {};

    if (!data || !metadata) continue;

    // Build a simple [start..end] page array from page_range, if present
    let segmentPages = [];
    if (Array.isArray(page_range) && page_range.length === 2) {
      const [start, end] = page_range;
      if (Number.isFinite(start) && Number.isFinite(end)) {
        for (let p = start; p <= end; p += 1) {
          segmentPages.push(p);
        }
      }
    }

    let globalCounter = 0; // shared counter across all entries
    const flat = [];

    for (const [key, value] of Object.entries(data)) {
      const child = flattenObject(value, metadata, {
        path: [key],
        id: key,
        level: 0,
        counter: globalCounter,
      });

      // Attach segment + page fallback + bracket-aware stringPath
      const withSegment = child.result.map(f => {
        const pathArray = Array.isArray(f.path)
          ? f.path
          : f.path != null
            ? [String(f.path)]
            : [];

        const stringPath =
          f.stringPath ??
          (pathArray.length ? pathPartsToFlatKey(pathArray) : '');

        return {
          ...f,
          segment: segmentKey,
          stringPath, // <-- bracket form like 'person[0].name'
          pages:
            f.pages && f.pages.length
              ? f.pages
              : segmentPages,
        };
      });

      flat.push(...withSegment);
      globalCounter = child.counter;
    }

    allFields.push(...flat);
  }

  return allFields;
};



// export const transformDataIntoFields = json => {
//   const allFields = [];

//   for (const value of Object.values(json)) {
//     const { data: mainData } = value;
//     const { data, metadata } = mainData;

//     let globalCounter = 0; // shared counter across all entries
//     const flat = [];

//     for (const [key, value] of Object.entries(data)) {
//       const child = flattenObject(value, metadata, {
//         path: [key],
//         id: key,
//         level: 0,
//         counter: globalCounter,
//       });
//       flat.push(...child.result);
//       globalCounter = child.counter;
//     }

//     allFields.push(...flat);
//   }

//   return allFields;
// };

const toPathString = path =>
  Array.isArray(path) ? path.join('.') : String(path ?? '');

// eslint-disable-next-line no-nested-ternary
const toPagesArray = p => Array.isArray(p) ? p : (p == null ? [] : [p]);

const COLORS = [
  '#a6cee3',
  '#1f78b4',
  '#b2df8a',
  '#33a02c',
  '#fb9a99',
  '#e31a1c',
  '#fdbf6f',
  '#ff7f00',
  '#cab2d6',
  '#6a3d9a',
  // '#ffff99',
  '#b15928',
];

export const flattenObject = (
    obj,
    allMetadata,
    { path = [], stringPath = '', id = null, level = 0, counter = 0, l = null, clr = null } = {},
) => {
  const result = [];
  let c = counter; // track counter locally
  const label = l ?? prettifyMixedCase(id);
  // const metadataKey = toPathString(path);
  const metadataKey = pathPartsToFlatKey(path);
  const metadata =
    metadataKey in allMetadata ? allMetadata[metadataKey] : {};
  const color = COLORS[c % COLORS.length];
  const ownPages = toPagesArray(metadata?.page);

  // Handle arrays
  if (Array.isArray(obj)) {
    c += 1;

    // create node first, push, then fill pages after children
    const node = {
      path: [...path],
      stringPath: toPathString([...path]),
      id,
      level,
      type: 'array',
      counter: c,
      label,
      metadata,
      color,
      pages: [], // will fill below
    };
    result.push(node);

    // collect pages from children
    const pagesSet = new Set(ownPages);

    for (let index = 0; index < obj.length; index++) {
      const child = flattenObject(obj[index], allMetadata, {
        path: [...path, index],
        stringPath: toPathString([...path, index]),
        id: index,
        level: level + 1,
        counter: c,
        l: `Item ${index + 1}`,
        clr: color,
      });

      result.push(...child.result);
      c = child.counter;

      // union child pages
      if (child.pages && child.pages.size) {
        child.pages.forEach(p => pagesSet.add(p));
      }
    }

    node.pages = Array.from(pagesSet).sort((a, b) => a - b); // ⬅️ assign pages
    return { result, counter: c, pages: pagesSet };
  }

  // Handle plain objects
  if (obj !== null && typeof obj === 'object') {
    c += 1;

    const node = {
      path: [...path],
      stringPath: toPathString([...path]),
      id,
      level,
      type: 'object',
      counter: c,
      label,
      metadata,
      color,
      pages: [],
    };
    result.push(node);

    const pagesSet = new Set(ownPages);

    for (const [key, value] of Object.entries(obj)) {
      const child = flattenObject(value, allMetadata, {
        path: [...path, key],
        stringPath: toPathString([...path, key]),
        id: key,
        level: level + 1,
        counter: c,
        clr: color,
      });

      result.push(...child.result);
      c = child.counter;

      if (child.pages && child.pages.size) {
        child.pages.forEach(p => pagesSet.add(p));
      }
    }

    node.pages = Array.from(pagesSet).sort((a, b) => a - b);
    return { result, counter: c, pages: pagesSet };
  }

  // Handle primitives
  c += 1;
  let type = typeof obj;
  if (obj === null) type = 'unknown';

  const pagesSet = new Set(ownPages);

  result.push({
    path: [...path],
    stringPath: toPathString([...path]),
    id,
    level,
    type,
    value: obj,
    counter: c,
    label,
    metadata,
    color,
    pages: Array.from(pagesSet).sort((a, b) => a - b), // leaf pages
  });

  return { result, counter: c, pages: pagesSet };
};

export function fullItemPath(path, id, index) {
  console.log(' path, id, index', path, id, index);
  let result = [];

  // normalize to array
  if (Array.isArray(path)) {
    result = [...path];
  } else if (typeof path === 'string' && path.length > 0) {
    result = [path];
  }

  // append next segment
  if (index !== null && index !== undefined) {
    result.push(index);
  } else if (id !== null && id !== undefined) {
    result.push(id);
  }

  return result;
}
